import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { useGame } from "@/context/GameContext";
import { fetchLeaderboard, postScore } from "@/lib/payments";
import Avatar from "@/components/avatar/Avatar";
import { Sound } from "@/lib/sound";
import { Trophy, Upload, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const MEDALS = ["🥇", "🥈", "🥉"];

const Row = ({ e, me, weekly }) => (
  <div
    className={`flex items-center gap-2 rounded-xl px-2 py-1.5 ${me ? "bg-amber-100 border-2 border-amber-400" : "bg-white/70 border border-slate-200"}`}
    data-testid={`lb-row-${e.rank}`}
  >
    <div className="w-7 text-center font-display font-bold text-slate-700">{MEDALS[e.rank - 1] || e.rank}</div>
    <div className="w-8 h-8 rounded-lg bg-slate-100 overflow-hidden flex items-end justify-center shrink-0">
      <Avatar avatar={e.avatar} size={30} className="translate-y-1" testid={`lb-avatar-${e.rank}`} />
    </div>
    <div className="flex-1 min-w-0">
      <div className="font-heading font-black text-sm text-slate-900 truncate">{e.name}{me ? " (you)" : ""}</div>
      <div className="text-[10px] font-bold text-slate-500">Day {e.day} · 🏆 {e.trophies}</div>
    </div>
    <div className="font-display font-bold text-emerald-700" data-testid={`lb-score-${e.rank}`}>{weekly ? "+" : ""}${e.score}</div>
  </div>
);

const LeaderboardCard = () => {
  const { state, ensureLeaderboardIdentity, bossScore } = useGame();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState("all");
  const score = bossScore();

  const load = async (which = tab) => {
    try { setData(await fetchLeaderboard(state.leaderboardId, which)); } catch (e) { setData({ entries: [], me: null, total: 0, error: true }); }
  };
  useEffect(() => { load(tab); }, [state.leaderboardId, tab]); // eslint-disable-line

  const post = async () => {
    setBusy(true);
    try {
      const me = await ensureLeaderboardIdentity();
      await postScore({
        ...me, name: state.playerName.slice(0, 12), avatar: state.avatar,
        score, profit: Math.max(0, Object.values(state.earnings).reduce((t, e) => t + (e.profit || 0), 0)),
        trophies: state.trophies, day: state.day,
      });
      const res = await fetchLeaderboard(me.player_id, tab);
      setData(res);
      Sound.fanfare();
      toast.success(res.me ? `You're #${res.me.rank} of ${res.total} bosses${tab === "weekly" ? " this week" : ""}!` : "Score posted!");
    } catch (e) {
      Sound.error();
      toast.error(e?.response?.status === 429 ? "Whoa, too fast! Try again in a few minutes." : "Couldn't post your score. Try again.");
    }
    setBusy(false);
  };

  const inTop = data?.me && data.entries.some((e) => e.player_id === data.me.player_id);

  return (
    <div className="bg-amber-50 rounded-3xl border-4 border-amber-300 p-5 card-3d" data-testid="leaderboard-card">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <div className="text-[10px] font-black uppercase tracking-widest text-amber-700">Global leaderboard</div>
          <h3 className="font-display text-xl font-bold text-slate-900 leading-tight flex items-center gap-1"><Trophy className="w-5 h-5 text-amber-500" /> Top Bosses</h3>
          <p className="text-xs font-bold text-slate-600 mt-1">Boss Score = your cash + piggy bank. Yours now: <span className="text-emerald-700" data-testid="my-boss-score">${score}</span></p>
        </div>
        <button onClick={load} data-testid="lb-refresh-btn" className="text-slate-400 hover:text-slate-700 p-1" title="Refresh"><RefreshCw className="w-4 h-4" /></button>
      </div>

      <div className="flex gap-1.5 mb-3" data-testid="lb-tabs">
        {[["all", "All-time"], ["weekly", "This week"]].map(([id, label]) => (
          <button
            key={id}
            onClick={() => { Sound.tap(); setTab(id); setData(null); }}
            data-testid={`lb-tab-${id}`}
            className={`px-3 py-1.5 rounded-xl border-2 text-xs font-heading font-black ${tab === id ? "bg-amber-400 border-amber-600 text-slate-900" : "bg-white border-slate-200 text-slate-600"}`}
          >
            {label}
          </button>
        ))}
      </div>
      {tab === "weekly" && (
        <p className="text-[11px] font-bold text-slate-500 mb-2" data-testid="lb-weekly-hint">Ranks money gained since Monday — resets every week so new bosses can win!</p>
      )}
      <div className="space-y-1.5 mb-3" data-testid="lb-list">
        {data === null && <div className="text-sm font-bold text-slate-500">Loading…</div>}
        {data?.error && <div className="text-sm font-bold text-rose-600">Leaderboard is taking a nap. Try refresh.</div>}
        {data && !data.error && data.entries.length === 0 && <div className="text-sm font-bold text-slate-500">{tab === "weekly" ? "Nobody has posted this week yet — post to lead the board!" : "No scores yet — be the first boss on the board!"}</div>}
        {data?.entries.map((e) => <Row key={e.player_id} e={e} weekly={tab === "weekly"} me={data.me && e.player_id === data.me.player_id} />)}
        {data?.me && !inTop && (
          <>
            <div className="text-center text-xs font-black text-slate-400">···</div>
            <Row e={data.me} me weekly={tab === "weekly"} />
          </>
        )}
      </div>

      <Button onClick={post} disabled={busy || !state.playerName} data-testid="lb-post-btn" className="w-full bg-amber-400 hover:bg-amber-500 text-slate-900 font-heading font-black rounded-2xl btn-3d border-2 border-amber-500 py-5">
        <Upload className="w-4 h-4 mr-1" /> {data?.me ? "Update my score" : "Post my score"}
      </Button>
      <p className="text-[10px] text-slate-500 font-semibold mt-2">Only your first name and avatar are shown. Ask a grown-up before posting.</p>
    </div>
  );
};

export default LeaderboardCard;
